<template>
  <div class="w-100 m-auto">
    <AuditLogSearchForm />

    <EasyDataTable
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow no-overflow"
      :server-items-length="count"
      :loading="loading"
      :headers="headers"
      :items="logs"
      :prevent-context-menu-row="false"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
      @update:server-options="updateServerOptions"
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #no-data>
        <div class="text-center">No activity logs found</div>
      </template>
      <template #expand="item">
        <div style="padding: 15px">
          <AuditLogDetails :log="item" />
        </div>
      </template>
      <template #item="{ column, item }: { column: string; item: AuditLogItem }">
        <span v-if="column === 'createdAt'">
          <RowDate :date="item.createdAt" />
        </span>
        <span v-if="column === 'action'">
          <span :class="['badge', actionBadgeClass(item.action)]">{{ item.action }}</span>
        </span>
        <span v-if="column === 'entityType'">{{ formatEntityType(item.entityType) }}</span>
        <span v-if="column === 'entityId'" class="text-monospace small">{{ item.entityId }}</span>
        <span v-if="column === 'user'">{{ userEmail(item) }}</span>
        <span v-if="column === 'ipAddress'">{{ item.ipAddress || '-' }}</span>
      </template>
    </EasyDataTable>

    <TablePagination
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="count"
    />
  </div>
</template>

<script lang="ts">
import type { AuditLogItem } from '@/types/Models/AuditLog'
import type { ComputedRef, Ref } from 'vue'
import { BSpinner } from 'bootstrap-vue-next'
import { ref, computed, watch, onMounted, onUnmounted, defineComponent } from 'vue'
import { normalizeName } from '@/services/Utils'
import { useAuditLogStore } from '@/store/auditLog'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import RowDate from '@/views/Components/Elements/Table/RowDate.vue'
import AuditLogDetails from '@/views/Staff/AuditLog/Details.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'
import AuditLogSearchForm from '@/views/Staff/AuditLog/SearchForm.vue'

export default defineComponent({
  name: 'AuditLogIndex',
  components: { BSpinner, EasyDataTable, RowDate, TablePagination, AuditLogDetails, AuditLogSearchForm },
  setup() {
    const auditLogStore = useAuditLogStore()
    const dataTable = ref()

    const loading: ComputedRef<boolean> = computed(() => auditLogStore.loading)
    const logs: ComputedRef<AuditLogItem[]> = computed(() => auditLogStore.logs)
    const count: ComputedRef<number> = computed(() => auditLogStore.count)

    const serverOptions: Ref<ServerOptions> = ref({
      page: 1,
      rowsPerPage: 50,
      sortBy: 'createdAt',
      sortType: 'desc',
    })

    const headers: Header[] = [
      { text: 'Date', value: 'createdAt', sortable: true },
      { text: 'Action', value: 'action', sortable: true },
      { text: 'Entity Type', value: 'entityType', sortable: true },
      { text: 'Entity ID', value: 'entityId' },
      { text: 'User', value: 'user' },
      { text: 'IP Address', value: 'ipAddress' },
    ]

    const formatEntityType = (type: string): string => normalizeName(type)

    const userEmail = (item: AuditLogItem): string => item.user?.email ?? '-'

    const actionBadgeClass = (action: string): string => {
      switch (action) {
        case 'created':
          return 'bg-success'
        case 'updated':
          return 'bg-warning text-dark'
        case 'deleted':
          return 'bg-danger'
        default:
          return 'bg-secondary'
      }
    }

    const syncServerOptions = () => {
      auditLogStore.updateFilter({
        page: String(serverOptions.value.page),
        limit: String(serverOptions.value.rowsPerPage),
        orderBy: serverOptions.value.sortBy || 'createdAt',
        ascending: serverOptions.value.sortType === 'asc' ? '1' : '0',
      })
    }

    const updateServerOptions = (newOptions: ServerOptions) => {
      serverOptions.value = newOptions
    }

    watch(serverOptions, syncServerOptions, { deep: true })

    onMounted(() => {
      auditLogStore.updateFilter({
        page: String(serverOptions.value.page),
        limit: String(serverOptions.value.rowsPerPage),
        orderBy: 'createdAt',
        ascending: '0',
      })
    })

    onUnmounted(() => {
      auditLogStore.unmount()
    })

    return {
      dataTable,
      loading,
      logs,
      count,
      headers,
      serverOptions,
      formatEntityType,
      userEmail,
      actionBadgeClass,
      updateServerOptions,
    }
  },
})
</script>

